import { useState } from 'react';
import { ColorLens as ColorLensIcon, Delete as DeleteIcon, Refresh as RefreshIcon, Save as SaveIcon } from '@mui/icons-material';
import { Avatar, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, List, ListItem, ListItemButton, ListItemText, Modal, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';
import moment from 'moment';
import type { CalendarColorPalette } from 'dashboard409a/common/constants/calendar';
import { toSolidColor, useColorPalette } from 'dashboard409a/hooks/useColorPalette';
import { getInitials } from 'utilities/getInitials';

const ColorSelector = () => {
  const {
    colorPalette,
    presets,
    savePresetOpen,
    presetName,
    setPresetName,
    handleColorChange,
    handleReset,
    handleSavePreset,
    handleOpenSavePreset,
    handleCloseSavePreset,
    handleApplyPreset,
    handleDeletePreset,
  } = useColorPalette();
  
  const [open, setOpen] = useState(false);

  const handleClose = () => {
    setOpen(false);
  };

  const onDeletePreset = (name: string, e: React.MouseEvent) => {
    e.stopPropagation();
    handleDeletePreset(name);
  };

  const statusLabels: Record<keyof CalendarColorPalette, string> = {
    DRAFT: 'Draft',
    REVIEW: 'Review',
    IN_PROCESS_SCALAR_USA: 'In Process ScalarUSA',
    FINAL: 'Final',
    COMPLETE: 'Complete',
    WAITING: 'Waiting',
    BACKGROUND: 'Background',
    BORDER: 'Border',
    LABELS: 'Labels',
  };


  return (
    <>
      <Tooltip title="Change Calendar Colors" arrow>
        <IconButton onClick={() => setOpen(true)} sx={{ color: 'primary.main', fontSize: 20 }}>
          <ColorLensIcon />
        </IconButton>
      </Tooltip>
      <Modal
        open={open}
        onClose={handleClose}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            p: 3,
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'auto',
            backgroundColor: 'white',
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" color="primary">
              Calendar Color Palette
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                startIcon={<RefreshIcon />}
                onClick={handleReset}
                size="small"
                variant="outlined"
              >
                Reset
              </Button>
            </Box>
          </Box>
          {presets.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Saved Presets
              </Typography>
              <List dense sx={{ border: '1px solid #e0e0e0', borderRadius: 1, maxHeight: 200, overflow: 'auto' }}>
                {presets.map(preset => (
                  <ListItem
                    key={preset.name}
                    disablePadding
                    secondaryAction={
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={e => onDeletePreset(preset.name, e)}
                        sx={{ mr: 1 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    }
                  >
                    <ListItemButton onClick={() => handleApplyPreset(preset.name)}>
                      <ListItemText primary={preset.name} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
          <Button
            startIcon={<SaveIcon />}
            onClick={handleOpenSavePreset}
            size="small"
            variant="outlined"
          >
            Save Preset
          </Button>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Model Status Colors
            </Typography>
            <Stack spacing={1}>
              {(['DRAFT', 'REVIEW', 'IN_PROCESS_SCALAR_USA', 'FINAL', 'COMPLETE', 'WAITING'] as const).map(key => (
                <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 120,
                      height: 40,
                      backgroundColor: colorPalette[key],
                      border: '1px solid #ccc',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#000',
                        fontWeight: 600,
                      }}
                    >
                      {statusLabels[key]}
                    </Typography>
                  </Box>
                  <TextField
                    type="color"
                    value={toSolidColor(colorPalette[key])}
                    onChange={e => handleColorChange(key, e.target.value)}
                    size="small"
                    sx={{ width: 80 }}
                  />
                  <TextField
                    value={colorPalette[key]}
                    onChange={e => handleColorChange(key, e.target.value)}
                    size="small"
                    sx={{ width: 120 }}
                    placeholder="#hexcolor"
                  />
                </Box>
              ))}
            </Stack>
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Calendar Style Colors
            </Typography>
            <Stack spacing={1}>
              {(['BACKGROUND', 'BORDER', 'LABELS'] as const).map(key => (
                <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 120,
                      height: 40,
                      backgroundColor: colorPalette[key],
                      border: '1px solid #ccc',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#000',
                        fontWeight: 600,
                      }}
                    >
                      {statusLabels[key]}
                    </Typography>
                  </Box>
                  <TextField
                    type="color"
                    value={toSolidColor(colorPalette[key])}
                    onChange={e => handleColorChange(key, e.target.value)}
                    size="small"
                    sx={{ width: 80 }}
                  />
                  <TextField
                    value={colorPalette[key]}
                    onChange={e => handleColorChange(key, e.target.value)}
                    size="small"
                    sx={{ width: 120 }}
                    placeholder="#hexcolor"
                  />
                </Box>
              ))}
            </Stack>
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Preview
            </Typography>
            <Box
              sx={{
                width: '190px',
                minHeight: '90px',
                display: 'flex',
                flexDirection: 'column',
                padding: 0.5,
                backgroundColor: colorPalette.BACKGROUND,
                border: `1px solid ${colorPalette.BORDER}`,
                borderRadius: 1,
              }}
            >
              <Box
                sx={{
                  backgroundColor: colorPalette.DRAFT,
                  padding: 0.5,
                  borderRadius: 1,
                  width: 50,
                  height: 10,
                  mb: 0.5,
                }}
              />
              <Box display="flex" flexDirection="row" justifyContent="space-between">
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  sx={{
                    lineHeight: 1.2,
                    color: '#333333',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Dunder Mifflin, Inc
                </Typography>
              </Box>
              <Box
                display="flex"
                justifyContent="space-between"
                sx={{ padding: 0.5, borderRadius: 1, flexWrap: 'wrap', gap: 0.5 }}
              >
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="space-between"
                  sx={{
                    color: 'white',
                    backgroundColor: colorPalette.LABELS,
                    padding: 0.5,
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="caption" fontSize="0.7rem" sx={{ color: 'white' }}>
                    Consulting
                  </Typography>
                </Box>
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="space-between"
                  sx={{
                    backgroundColor: colorPalette.LABELS,
                    padding: 0.5,
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="caption" fontSize="0.7rem" sx={{ color: 'white' }}>
                    {moment().format('MMM, DD, YY')}
                  </Typography>
                </Box>
              </Box>
              <Box display="flex" flexDirection="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
                <Avatar sx={{ width: 20, height: 20, fontSize: '0.75rem' }}>
                  {getInitials({ name: 'John Rambo' })}
                </Avatar>
                <Avatar sx={{ width: 20, height: 20, fontSize: '0.75rem' }}>
                  {getInitials({ name: 'Sylvester Stallone' })}
                </Avatar>
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button onClick={handleClose} variant="contained">
              Done
            </Button>
          </Box>
        </Paper>
      </Modal>
      <Dialog open={savePresetOpen} onClose={handleCloseSavePreset}>
        <DialogTitle>Save Color Palette Preset</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Preset Name"
            fullWidth
            variant="outlined"
            value={presetName}
            onChange={e => setPresetName(e.target.value)}
            onKeyPress={e => {
              if (e.key === 'Enter' && presetName.trim()) {
                handleSavePreset();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSavePreset}>Cancel</Button>
          <Button onClick={handleSavePreset} variant="contained" disabled={!presetName.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ColorSelector;